import React, { createContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AlertType } from '../components/alertComponent';
import {
  CustomNotificationActions,
  useHandleExtraActions,
} from '../hooks/useHandleExtraActions';
import { useNavigate } from 'react-router-dom';

export type CustomToastAction = {
  label: string;
  route?: string | null;
  customAction?: CustomNotificationActions | null;
  customActionParams?: Record<string, unknown> | null;
};

export interface Toast {
  id: string;
  message: string | React.ReactNode;
  type: AlertType;
  showCloseButton?: boolean;
  action?: CustomToastAction;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (
    toast: Omit<Toast, 'id'> & { id?: Toast['id'] },
  ) => string | undefined;
  removeToast: (id: string) => void;
  handleAction: (
    notificationId: string,
    route?: string | null,
    customAction?: CustomNotificationActions | null,
    customActionParams?: Record<string, unknown> | null,
    isCloseButton?: boolean,
  ) => Promise<void>;
}

export const ToastContext = createContext<ToastContextValue | undefined>(
  undefined,
);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { handler: customActionsHandler } = useHandleExtraActions();
  const navigate = useNavigate();

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'> & { id?: Toast['id'] }) => {
      const isDuplicate = toasts.find(({ id }) => id === toast.id);
      if (isDuplicate) return;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const newToast = { ...toast, id: toast.id || uuidv4() };
      setToasts((prev) => [...prev, newToast]);

      if (newToast.showCloseButton) return newToast.id;

      // Auto-remove after 5s
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
      }, 5000);

      return newToast.id;
    },
    [toasts],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleAction = async (
    notificationId: string,
    route?: string | null,
    customAction?: CustomNotificationActions | null,
    customActionParams?: Record<string, unknown> | null,
    isCloseButton = false,
  ) => {
    try {
      if (isCloseButton) {
        removeToast(String(notificationId));
        return;
      }
      if (customAction && customActionsHandler[customAction]) {
        await customActionsHandler[customAction](
          customActionParams ?? {},
          false,
        );
        removeToast(String(notificationId));
      } else {
        removeToast(String(notificationId));
        navigate((route || '') ?? -1);
      }
    } catch (error: unknown) {
      throw new Error('Failed to update notification');
    }
    return;
  };

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, handleAction }}
    >
      {children}
    </ToastContext.Provider>
  );
};
